import React, { useState } from 'react';
import { useDarkMode } from '../../../../context/DarkModeContext';
import { InternshipResult, OfferDetails } from '../../../../types/internship';
import { CareerFocusWithMetrics } from '../../../../hooks/useCareerFocus';
import { CalendarIcon, MapPinIcon, UserGroupIcon } from '@heroicons/react/24/outline';

interface OfferDetailsFormProps {
  data: Partial<InternshipResult>;
  onUpdate: (updates: Partial<InternshipResult>) => void;
  focus: CareerFocusWithMetrics;
}

const OfferDetailsForm: React.FC<OfferDetailsFormProps> = ({
  data,
  onUpdate
}) => {
  const { darkMode } = useDarkMode();
  const [offerDetails, setOfferDetails] = useState<Partial<OfferDetails>>(
    data.offerDetails || {}
  );
  const [decisionStatus, setDecisionStatus] = useState(data.decisionStatus || 'pending');
  const [decisionDeadline, setDecisionDeadline] = useState(
    data.decisionDeadline ? new Date(data.decisionDeadline).toISOString().split('T')[0] : ''
  );
  
  const bgColor = darkMode ? 'bg-gray-700' : 'bg-gray-50';
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const inputBg = darkMode ? 'bg-gray-800' : 'bg-white';
  const borderColor = darkMode ? 'border-gray-600' : 'border-gray-300';
  
  const updateOfferDetail = (key: keyof OfferDetails, value: any) => {
    setOfferDetails(prev => ({ ...prev, [key]: value }));
  };
  
  // Update parent data whenever local state changes
  React.useEffect(() => {
    // Only update if values have actually changed from what was passed in
    const hasChanges = 
      JSON.stringify(offerDetails) !== JSON.stringify(data.offerDetails || {}) ||
      decisionStatus !== (data.decisionStatus || 'pending') ||
      decisionDeadline !== (data.decisionDeadline ? new Date(data.decisionDeadline).toISOString().split('T')[0] : '');
    
    if (hasChanges) {
      onUpdate({
        offerDetails,
        decisionStatus,
        decisionDeadline: decisionDeadline || undefined
      });
    }
  }, [offerDetails, decisionStatus, decisionDeadline]);
  
  return (
    <div className="space-y-6">
      <div>
        <h2 className={`text-xl font-semibold mb-2 ${textColor}`}>
          Congratulations on your offer! 🎉
        </h2>
        <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Let's capture the details of your offer (all fields are optional)
        </p>
      </div>
      
      {/* Decision Status */}
      <div>
        <label className={`block text-sm font-medium mb-2 ${textColor}`}>
          Have you made a decision?
        </label>
        <div className="space-y-2">
          {[
            { value: 'accepted', label: 'Yes, I accepted the offer' },
            { value: 'declined', label: 'Yes, I declined the offer' },
            { value: 'pending', label: 'Still deciding' }
          ].map(option => (
            <label key={option.value} className="flex items-center">
              <input
                type="radio"
                value={option.value}
                checked={decisionStatus === option.value}
                onChange={(e) => setDecisionStatus(e.target.value as any)}
                className="mr-3"
              />
              <span className={textColor}>{option.label}</span>
            </label>
          ))}
        </div>
      </div>
      
      {/* Decision Deadline */}
      {decisionStatus === 'pending' && (
        <div>
          <label className={`block text-sm font-medium mb-2 ${textColor}`}>
            Decision Deadline
          </label>
          <div className="relative">
            <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="date"
              value={decisionDeadline}
              onChange={(e) => setDecisionDeadline(e.target.value)}
              className={`w-full pl-10 pr-3 py-2 rounded-lg border ${borderColor} ${inputBg} ${textColor}`}
            />
          </div>
        </div>
      )}
      
      {/* Offer Details */}
      <div className={`p-4 rounded-lg ${bgColor}`}>
        <h3 className={`font-medium mb-4 ${textColor}`}>Offer Details (Optional)</h3>
        
        <div className="space-y-4">
          {/* Start Date */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${textColor}`}>
              Start Date
            </label>
            <div className="relative">
              <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="date"
                value={offerDetails.startDate ? new Date(offerDetails.startDate).toISOString().split('T')[0] : ''}
                onChange={(e) => updateOfferDetail('startDate', e.target.value)}
                className={`w-full pl-10 pr-3 py-2 rounded-lg border ${borderColor} ${inputBg} ${textColor}`}
              />
            </div>
          </div>
          
          {/* Team */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${textColor}`}>
              Team/Department
            </label>
            <div className="relative">
              <UserGroupIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={offerDetails.team || ''}
                onChange={(e) => updateOfferDetail('team', e.target.value)}
                placeholder="e.g., Cloud Infrastructure"
                className={`w-full pl-10 pr-3 py-2 rounded-lg border ${borderColor} ${inputBg} ${textColor}`}
              />
            </div>
          </div>
          
          {/* Location */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${textColor}`}>
              Location
            </label>
            <div className="relative">
              <MapPinIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={offerDetails.location || ''}
                onChange={(e) => updateOfferDetail('location', e.target.value)}
                placeholder="e.g., Seattle, WA"
                className={`w-full pl-10 pr-3 py-2 rounded-lg border ${borderColor} ${inputBg} ${textColor}`}
              />
            </div>
          </div>
          
          {/* Location Type */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${textColor}`}>
              Work Arrangement
            </label>
            <select
              value={offerDetails.locationType || ''}
              onChange={(e) => updateOfferDetail('locationType', e.target.value)}
              className={`w-full px-3 py-2 rounded-lg border ${borderColor} ${inputBg} ${textColor}`}
            >
              <option value="">Select...</option>
              <option value="onsite">On-site</option>
              <option value="remote">Remote</option>
              <option value="hybrid">Hybrid</option>
            </select>
          </div>
        </div>
      </div>
      
    </div>
  );
};

export default OfferDetailsForm;